import { AppConstants } from '@unpispas/upp-defs';
import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from "../base";
import { CreateObject } from "../item";
import { PreselectView, PreselectViewProxy } from '../views/preselect';
import { Product } from './product';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { ProductType } from '../types';
import { PreselectOptType } from '../types';
import { I18nValueType } from '../types';

const _schema = {
    "name": "PRESELECT",
    "fields": [
        { name: "status", type: "string", default: "AC" },
        { name: "product", type: "objid" },
        { name: "name", type: "objid" },
        { name: "photo", type: "file" },
        { name: "description", type: "objid" },
        { name: "code", type: "string" },
        { name: "barcode", type: "string" },
        { name: "alias", type: "string" },
        { name: "sort", type: "number" }
    ],
    relate: [
        { direction: ">", target: "PRODUCT", by: "product", name: "product", reason: "product", child: false, class: ProductType },
        { direction: ">", target: "I18NVALUE", by: "name", name: "name", reason: "name", child: true, class: I18nValueType },
        { direction: ">", target: "I18NVALUE", by: "description", name: "description", reason: "description", child: true, class: I18nValueType },
        { direction: "<", target: "PRESELECTOPT", by: "select", name: "option", reason: "options", child: true, class: PreselectOptType }
    ]
} as const;

const _PreselectClass = CreateObject(_schema);
if (!_PreselectClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class Preselect extends _PreselectClass { 
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): Preselect {
        return this._Copy(store) as Preselect;
    }

    override get viewProxy(): PreselectView {
        return PreselectViewProxy(this, this.data);
    }

    /**
     * A PRESELECT row arriving through `_CHANGESLOG` does not carry the parent PRODUCT children list
     * (`selects` only travels on full load), so `product.selects` would stay stale until a full reload.
     * Register in the parent when the `product` link is set (idempotent: `AddChild` ignores known children).
     *
     * Only rows already in the database register here: drafts (shortcut editor, previews, catalog)
     * join their product explicitly when accepted, and copies build their lists in `_Copy`.
     */
    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf || !this.Commited || relation !== 'product' || !(related instanceof Product)){
            return;
        }

        if (related === this.product){
            related.AddSelect(this);
        }
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get avatar(): string | null {
        const _bg = 'd3c79b';
        const _fg = '000000';
        const _lb = this.name?.tr() ?? null;

        if (!_lb){
            return null;
        }

        return AppConstants.baseURL + 'photo/avatar?size=128&background=' + _bg + '&color=' + _fg + '&name=' + encodeURIComponent(_lb);
    }

    get photo(): string | null {
        return this['__base__photo'] || this.avatar;
    }

    set photo(value: string | null){
        this['__base__photo'] = value;
    }

    get status(): string | null {
        return this['__base__status'];
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;     // cannot modify deleted items
        }

        if (this.status == value){
            return;
        }

        this['__base__status'] = value;

        if (this.product){
            if ((this.status == 'DE') && !this.Commited){
                this.product.DelSelect(this);
            }
            else {
                this.product.DoRefresh(this.table);
            }    
        }
    }

    /****************************/
    /* CATALOG CONSIDERATIONS   */
    /****************************/

    override get IsCatalog(): boolean {
        return super.IsCatalog;
    }

    override set IsCatalog(value: boolean){
        if (value == this.IsCatalog){
            return;
        }

        super.IsCatalog = value;
        if (value == false){
            if (this.status == 'CT'){
                this.status = 'AC';
            }

            for (const _child of this.Children){
                _child.IsCatalog = value;
            }
        }
    }
        
    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get IsValid(): boolean {
        return (!!this.status && (this.status != 'DE'));
    }

    get IsMarket() : boolean {
        return this.product?.IsMarket || false;
    }

    get IsWeighted() : boolean {
        return this.product?.IsWeighted || false;
    }
}

